import {LabelToken} from '@github-ui/label-token'
import {ListItemTrailingBadge} from '@github-ui/list-view/ListItemTrailingBadge'
import {ActionList, ActionMenu, Button, Tooltip} from '@primer/react'

import {TEST_IDS} from '../constants/test-ids'
import type {IssueFieldBadge} from './IssueFieldPills'
import {IssueFieldValueToken} from './IssueFieldValueBadge'
import styles from './MetadataOverflowMenu.module.css'

export type OverflowLabel = {
  id: string
  name: string
  color: string
}

type MetadataOverflowMenuProps = {
  hiddenFieldCount: number
  hiddenLabelCount: number
  fields: IssueFieldBadge[]
  labels: OverflowLabel[]
  getIssueFieldHref?: (fieldName: string, queryValue: string) => string
  getLabelHref: (name: string) => string
}

// `+N` pill that opens an accessible Primer ActionMenu listing the full set of
// fields and labels. Display + filter only: selecting a value navigates to the
// filtered list, mirroring a click on an inline pill.
export function MetadataOverflowMenu({
  hiddenFieldCount,
  hiddenLabelCount,
  fields,
  labels,
  getIssueFieldHref,
  getLabelHref,
}: MetadataOverflowMenuProps) {
  const count = hiddenFieldCount + hiddenLabelCount
  if (count <= 0) return null

  const description = 'Show all'

  return (
    <ListItemTrailingBadge title={description}>
      <ActionMenu>
        <ActionMenu.Anchor>
          <Tooltip direction="nw" text={description}>
            <Button variant="invisible" size="small" className={styles.overflowButton}>
              {`+${count}`}
            </Button>
          </Tooltip>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay align="end">
          <ActionList className={styles.overflowList} data-testid={TEST_IDS.listRowMetadataOverflowMenu}>
            {fields.length > 0 && (
              <ActionList.Group>
                <ActionList.GroupHeading>Fields</ActionList.GroupHeading>
                {fields.map(field => (
                  <ActionList.LinkItem
                    key={`overflow-field-${field.fieldName}-${field.displayValue}`}
                    href={getIssueFieldHref?.(field.fieldName, field.queryValue)}
                  >
                    <span className={styles.fieldItem}>
                      <span className={styles.fieldName}>{field.fieldName}</span>
                      <IssueFieldValueToken displayValue={field.displayValue} />
                    </span>
                  </ActionList.LinkItem>
                ))}
              </ActionList.Group>
            )}
            {fields.length > 0 && labels.length > 0 && <ActionList.Divider />}
            {labels.length > 0 && (
              <ActionList.Group>
                <ActionList.GroupHeading>Labels</ActionList.GroupHeading>
                {labels.map(label => (
                  <ActionList.LinkItem key={`overflow-label-${label.id}`} href={getLabelHref(label.name)}>
                    <LabelToken text={label.name} fillColor={`#${label.color}`} />
                  </ActionList.LinkItem>
                ))}
              </ActionList.Group>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </ListItemTrailingBadge>
  )
}
